import React, { useMemo, useState } from "react";
import { Plus, ArrowRight, ListChecks, Users, Mail, Send, FileText, Layers, FileSearch, StickyNote, Truck, Trophy, XCircle, Paperclip, CheckSquare, PackageCheck, BadgeCheck, Activity } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, relTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState } from "@/components/common";
import { cn } from "@/lib/utils";

const ICONS = {
  created: Plus,
  stage_change: ArrowRight,
  requirement: ListChecks,
  meeting: Users,
  email: Mail,
  quote_sent: Send,
  quote_created: FileText,
  solution: Layers,
  tender: FileSearch,
  note: StickyNote,
  supplier_quote: Truck,
  won: Trophy,
  lost: XCircle,
  file: Paperclip,
  task: CheckSquare,
  handover: PackageCheck,
  approval: BadgeCheck,
};

const TONE = {
  won: "bg-[hsl(var(--status-ok)/0.12)] text-[hsl(var(--status-ok))]",
  lost: "bg-[hsl(var(--status-bad)/0.12)] text-[hsl(var(--status-bad))]",
  quote_sent: "bg-primary/10 text-primary",
  handover: "bg-[hsl(var(--status-ok)/0.12)] text-[hsl(var(--status-ok))]",
  approval: "bg-primary/10 text-primary",
};

export const ActivityIcon = ({ type, className }) => {
  const Icon = ICONS[type] || Activity;
  return (
    <span className={cn("inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground", TONE[type], className)}>
      <Icon className="h-3.5 w-3.5" />
    </span>
  );
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "meeting", label: "Meetings", types: ["meeting"] },
  { key: "email", label: "E-mails", types: ["email"] },
  { key: "quote", label: "Quotes", types: ["quote_sent", "quote_created", "approval"] },
  { key: "requirement", label: "Requirements", types: ["requirement", "tender"] },
  { key: "note", label: "Notes", types: ["note"] },
  { key: "system", label: "System", types: ["created", "stage_change", "file", "solution", "supplier_quote", "won", "lost", "handover", "task"] },
];

export const ActivityTab = ({ data, reload }) => {
  const o = data.opportunity;
  const [filter, setFilter] = useState("all");
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);
  const users = useMemo(() => Object.fromEntries((data.users || []).map((u) => [u.id, u])), [data.users]);

  const list = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter);
    const acts = data.activities || [];
    if (!f || !f.types) return acts;
    return acts.filter((a) => f.types.includes(a.type));
  }, [data.activities, filter]);

  const addNote = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      await api.post(`/opportunities/${o.id}/activities`, { title: title.trim(), detail: detail.trim(), type: "note" });
      setTitle("");
      setDetail("");
      toast.success("Note added");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  // group by day
  const groups = useMemo(() => {
    const out = [];
    let cur = null;
    for (const a of list) {
      const day = (a.date || "").slice(0, 10);
      if (!cur || cur.day !== day) {
        cur = { day, items: [] };
        out.push(cur);
      }
      cur.items.push(a);
    }
    return out;
  }, [list]);

  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.6fr_1fr]">
      <div>
        <div className="mb-5 flex flex-wrap items-center gap-1.5" role="tablist">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" role="tab" onClick={() => setFilter(f.key)} data-testid={`activity-filter-${f.key}`} className={cn("rounded-full border px-3 py-1 text-xs font-medium", filter === f.key ? "border-foreground bg-foreground text-white" : "bg-white text-muted-foreground hover:text-foreground")}>
              {f.label}
            </button>
          ))}
        </div>
        {groups.length === 0 ? (
          <EmptyState icon={Activity} title="No activity yet" description="Everything that happens on this opportunity is logged here automatically." testId="activity-empty" />
        ) : (
          <ol className="relative space-y-8 before:absolute before:left-3 before:top-2 before:h-[calc(100%-1rem)] before:w-px before:bg-border" data-testid="activity-timeline">
            {groups.map((g) => (
              <li key={g.day} className="relative">
                <div className="mb-3 pl-10 text-xs font-medium uppercase tracking-wide text-muted-foreground">{relTime(g.day) === "today" ? "Today" : fmtDateTime(g.day + "T12:00:00").slice(0, -6)}</div>
                <ul className="space-y-4">
                  {g.items.map((a) => (
                    <li key={a.id} className="relative flex items-start gap-4" data-testid="activity-item">
                      <ActivityIcon type={a.type} className="relative z-10 ring-4 ring-background" />
                      <div className="min-w-0 flex-1 pt-0.5">
                        <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                          <div className="text-sm font-medium">{a.title}</div>
                          <div className="text-xs tabular text-muted-foreground" title={fmtDateTime(a.date)}>{relTime(a.date)}</div>
                        </div>
                        {a.detail ? <p className="mt-0.5 whitespace-pre-line text-sm leading-6 text-muted-foreground">{a.detail}</p> : null}
                        <div className="mt-1 text-xs text-muted-foreground/80">{a.user_id ? users[a.user_id]?.name || "Team" : "System / AI"}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        )}
      </div>
      <aside>
        <form onSubmit={addNote} className="rounded-2xl border bg-white p-4 hairline" data-testid="activity-note-form">
          <div className="text-sm font-semibold">Add a note</div>
          <p className="mt-0.5 text-xs text-muted-foreground">Calls, decisions, hallway conversations. The AI uses these when it prepares you.</p>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What happened?" className="mt-3 h-9 rounded-[10px]" data-testid="activity-note-title-input" />
          <Textarea value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Details (optional)" rows={3} className="mt-2 rounded-[10px] text-sm" data-testid="activity-note-detail-input" />
          <Button type="submit" size="sm" disabled={busy || !title.trim()} className="mt-3 gap-1.5 bg-primary text-white hover:bg-[#ff8e76]" data-testid="activity-note-submit-button"><Plus className="h-3.5 w-3.5" /> Add note</Button>
        </form>
        <div className="mt-6 text-xs text-muted-foreground">
          {data.activities?.length || 0} events · {(data.meetings || []).length} meetings · {(data.files || []).length} files
        </div>
      </aside>
    </div>
  );
};

export default ActivityTab;
